import { useEffect, useState, useCallback, useMemo } from 'react';
import { AbstractWallet, ETHEREUM_METHOD } from '@blink/components/src/utils';
import { ethers } from 'ethers';
import { useSelector } from 'react-redux';

import { Store } from '@/shared/store';
import IERCAbi from '@/shared/abi/IERCAbi.json';
import { Agreement } from '@/shared/api/borrow';
import { getSafeContract } from '@/shared/utils/getSafeContract';
import { getSafeSigner } from '@/shared/utils/getSafeSigner';
import { getWalletMap } from '@/shared/utils/getWalletMap';

type UseGetMaxBorrowProps = {
    agreement: Agreement;
};

export const useGetMaxBorrow = ({ agreement }: UseGetMaxBorrowProps) => {
    const [maxBorrow, setMaxBorrow] = useState('');
    const [maxBorrowLoading, setMaxBorrowLoading] = useState(true);

    const assetAddress = useMemo(() => agreement?.leverage?.address as string, [agreement]);

    const { type, blockchainName } = useSelector((state: Store) => ({
        blockchainName: state.wallets.network,
        walletAddress: state.wallets.active.address,
        type: state.wallets.active.type,
    }));

    const getMaxBorrow = useCallback(async () => {
        const walletMap = getWalletMap();
        const wallet: AbstractWallet = walletMap.get(type) as AbstractWallet;

        const walletProvider = await wallet.getProvider();

        await walletProvider.request({ method: ETHEREUM_METHOD.REQUEST });

        const signer = await getSafeSigner(walletProvider, blockchainName);

        const token = getSafeContract(
            agreement.leverage.address as string,
            IERCAbi,
            signer as ethers.Signer,
        );

        const tokenBalanceOf = await token.balanceOf(agreement.address);
        const decimals = await token.decimals();
        const tokenBalance = ethers.formatUnits(tokenBalanceOf, decimals);

        setMaxBorrow(tokenBalance);
        setMaxBorrowLoading(false);
    }, [agreement, type, blockchainName]);

    useEffect(() => {
        if (!assetAddress) {
            return;
        }
        getMaxBorrow();
    }, [assetAddress, getMaxBorrow]);

    return { data: maxBorrow, loading: maxBorrowLoading };
};
